import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    firstValueFrom,
    map,
    of,
    startWith,
    switchMap,
} from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'
import { EvidaFullRoutes } from 'src/app/evida/models/evida-routes.model'
import { InstallationItem } from 'src/app/evida/profile/models/installation-item.model'
import { ProductionEquipmentAccessService } from 'src/app/evida/profile/services/production-equipment-access.service'
import { ProfileStateService } from 'src/app/evida/profile/services/profile-state.service'
import { CapacitySubmissionService } from 'src/app/evida/capacity-submission/services/capacity-submission.service'
import { AdminService } from 'src/app/evida/services/admin-service'
import { hasCompleteInstallationData, isBiogasProduction } from 'src/app/utils/installation-utils'
import { AuthService } from '../auth/auth.service'
import { Menu, MenuItemId, SubmenuItem } from '../models/menu.model'

/** Default menu title for consumption development (Forbrugsudvikling) */
const MENU_TITLE_CONSUMPTION = 'menu.consumption.submenu.overview'
/** Menu title for biogas production (Biogasproduktion) */
const MENU_TITLE_BIOGAS_PRODUCTION = 'menu.consumption.submenu.overviewProduction'
/** Menu title for production readings when meter type is FORB (Produktionsudvikling) */
const MENU_TITLE_PRODUCTION = 'menu.consumption.submenu.production'

/**
 * Type guard to check if installation item data is complete and valid for menu decisions.
 * This prevents race conditions where meterUnits or meterPointTypeCode may be undefined during state transitions.
 * Wraps the shared hasCompleteInstallationData to work with InstallationItem.
 */
function hasValidInstallationData(installation: InstallationItem | null): installation is InstallationItem {
    return (
        installation !== null &&
        installation.selectedInstallation !== null &&
        installation.selectedInstallation !== undefined &&
        hasCompleteInstallationData(installation.selectedInstallation)
    )
}

@Injectable({
    providedIn: 'root',
})
export class MenuService {
    private menu: Menu[] = [
        new Menu(
            MenuItemId.frontpage,
            'menu.frontpage',
            false,
            false,
            'evida_menu_frontpage',
            `${EvidaFullRoutes.frontpage}`
        ),
        new Menu(MenuItemId.documents, 'menu.documents.title', false, false, 'evida_menu_documents', null, [
            {
                id: MenuItemId.invoice,
                title: 'menu.documents.submenu.bill',
                routerLink: EvidaFullRoutes.invoice,
                selected: false,
            },
        ]),
        new Menu(MenuItemId.consumption, 'menu.consumption.title', false, false, 'evida_menu_consumption', null, [
            {
                id: MenuItemId.development,
                title: MENU_TITLE_CONSUMPTION,
                routerLink: EvidaFullRoutes.development,
                selected: false,
            },
            {
                id: MenuItemId.productionreadings,
                title: MENU_TITLE_PRODUCTION,
                routerLink: EvidaFullRoutes.productionreadings,
                selected: false,
                hidden: true,
            },
            {
                id: MenuItemId.capacitySubmission,
                title: 'menu.consumption.submenu.capacitySubmission',
                routerLink: EvidaFullRoutes.capacitySubmission,
                selected: false,
                hidden: true,
            },
        ]),
        new Menu(MenuItemId.profile, 'menu.profile.root', false, false, 'evida_menu_profile', null, [
            {
                id: MenuItemId.myprofile,
                title: 'menu.profile.myprofile',
                routerLink: EvidaFullRoutes.profile,
                selected: false,
            },
            { id: MenuItemId.admin, title: 'menu.profile.admin', routerLink: EvidaFullRoutes.admin, selected: false },
        ]),
        new Menu(MenuItemId.logout, 'menu.logout', false, false, 'evida_menu_logout', EvidaFullRoutes.logout),
    ]
    private isOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
    private menuItems: BehaviorSubject<Menu[]> = new BehaviorSubject<Menu[]>(this.menu)
    private capacitySubmissionVisibility: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
    public isOpen$: Observable<boolean> = this.isOpen.asObservable()
    public menuItems$: Observable<Menu[]> = this.menuItems.asObservable()
    public capacitySubmissionVisible$: Observable<boolean> = this.capacitySubmissionVisibility.asObservable()

    public constructor(
        private authsService: AuthService,
        private productionEquipmentAccessService: ProductionEquipmentAccessService,
        private router: Router,
        private adminService: AdminService,
        private profileStateService: ProfileStateService,
        private capacitySubmissionService: CapacitySubmissionService
    ) {
        if (this.authsService.isBusinessCustomer) {
            // Combine productionEquipment$ with selectedInstallation$ to determine if "Bioproduktion" menu should be shown
            // The menu should only be shown for biogas producers, NOT for el/varme producers (varmeværker)
            // Filter out invalid installation data to prevent race conditions where meterUnits is undefined
            combineLatest([
                this.productionEquipmentAccessService.productionEquipment$,
                this.profileStateService.selectedInstallation$,
            ])
                .pipe(filter(([_, installation]) => hasValidInstallationData(installation)))
                .subscribe(([hasProductionEquipment, installation]) => {
                    // Show the production readings menu only for FORB el/varme producers
                    // PROD (biogas) installations do NOT show this menu item
                    const meterPointTypeCode = installation?.selectedInstallation?.meterPointTypeCode
                    const showProductionMenu = hasProductionEquipment && meterPointTypeCode !== 'PROD'

                    this.menu
                        .filter((menuItem) => menuItem.id === MenuItemId.consumption)
                        .forEach((item) =>
                            item.submenu.forEach((i) => {
                                if (i.id === MenuItemId.productionreadings) {
                                    i.hidden = !showProductionMenu
                                    if (i.hidden && i.selected) {
                                        this.selectSubmenuItmById(MenuItemId.development)
                                        this.router.navigate([EvidaFullRoutes.development])
                                    }
                                }
                            })
                        )

                    // Notify subscribers about the menu change
                    this.menuItems.next(this.menu)
                })
        }

        // Update title and capacity visibility for the selected installation.
        combineLatest([
            this.profileStateService.selectedInstallation$.pipe(
                map((installation) => {
                    if (hasValidInstallationData(installation)) {
                        this.updateDevelopmentMenuTitle(isBiogasProduction(installation.selectedInstallation))
                    }

                    const installationNumber = installation?.selectedInstallation?.installationNumber
                    return typeof installationNumber === 'string' && installationNumber.trim()
                        ? installationNumber
                        : null
                }),
                distinctUntilChanged(),
                switchMap((installationNumber) => {
                    if (!this.authsService.isBusinessCustomer || !installationNumber) {
                        return of(null)
                    }

                    return this.capacitySubmissionService.getStatus(installationNumber).pipe(
                        catchError(() => of(null)),
                        startWith(null)
                    )
                })
            ),
            this.productionEquipmentAccessService.productionEquipment$,
        ]).subscribe(([status]) => {
            // The BFF status is authoritative for capacity submission access.
            // currentCapacity is present only when the selected installation has
            // one valid Kaptime entry in the BFF aggregate.
            this.setCapacitySubmissionVisibility(
                Boolean(status?.currentCapacity != null && (status.available || status.eligible))
            )
        })

        // Keep private-user admin menu behavior independent from capacity status updates.
        this.profileStateService.customerNumber$.pipe(distinctUntilChanged()).subscribe(async (x) => {
            const users =
                (await firstValueFrom(this.adminService.getAssociatedUser$(x, this.authsService.isBusinessCustomer)))
                    .associatedUsers ?? []
            const loggedInUser = users.find((x) => x.mitId === this.authsService.mitIdUuid)

            if (!this.authsService.isIdpUser) {
                this.menu
                    .filter((x) => x.id === MenuItemId.profile)
                    .forEach((x) =>
                        x.submenu.filter((y) => {
                            if (y.id === MenuItemId.admin) {
                                y.hidden =
                                    users.length === 0 || //<-- non-admin users does not get user list from backend
                                    !(loggedInUser?.isAdmin ?? false) //<--- non-IDP must be admin to see the menu
                            }
                        })
                    )
            }
        })
    }

    private setCapacitySubmissionVisibility(visible: boolean): void {
        this.menu
            .filter((menuItem) => menuItem.id === MenuItemId.consumption)
            .flatMap((menuItem) => menuItem.submenu)
            .filter((item) => item.id === MenuItemId.capacitySubmission)
            .forEach((item) => (item.hidden = !visible))
        this.capacitySubmissionVisibility.next(visible)
        this.menuItems.next(this.menu)
    }

    public menuItemSelected(menuItem: Menu) {
        this.deselectMenuItems(menuItem.id)
        menuItem.selected = !menuItem.selected

        if (menuItem.submenu === undefined) {
            this.deselectSubmenuItems()
            menuItem.active = true
        }
    }

    public submenuItemSelected(menuItem: SubmenuItem) {
        this.deselectSubmenuItems()
        menuItem.selected = true
    }

    public selectSubmenuItmById(id: MenuItemId) {
        this.menu
            .filter((items) => items.submenu !== undefined)
            .forEach((parentItem) =>
                parentItem.submenu
                    .filter((childItem) => childItem.id === id)
                    .map((i) => {
                        i.selected = true
                        parentItem.active = true
                        parentItem.selected = true
                    })
            )
    }

    public async preselectedMenuItem(id: MenuItemId) {
        // Deselect all menues items
        this.menu.forEach((item) => {
            item.selected = false
            item.deselectSubmenuItems()
        })
        const menuItem = this.menu.find((items) => items.id === id)
        if (menuItem) {
            this.menuItemSelected(menuItem)
        } else {
            this.selectSubmenuItmById(id)
        }
    }

    // Used by burgermenu on mobile devices
    public handleMenu() {
        const isMenuOpen = this.isOpen.getValue()
        this.disableScrollForBurgerMenu(isMenuOpen)
        this.isOpen.next(!isMenuOpen)
    }

    public close() {
        if (this.isOpen.getValue()) {
            this.isOpen.next(false)
            this.disableScrollForBurgerMenu(true)
        }
    }

    public setBurgerMenuOverflowToScroll() {
        this.disableScrollForBurgerMenu(true)
    }

    private deselectMenuItems(id: MenuItemId) {
        this.menu.forEach((item) => {
            item.deselectMenuItems(id)
        })
    }

    private deselectSubmenuItems() {
        this.menu.forEach((item) => {
            item.deselectSubmenuItems()
        })
    }

    private disableScrollForBurgerMenu(disable: boolean) {
        if (disable) {
            document.body.classList.remove('burger-menu')
        } else {
            document.body.classList.add('burger-menu')
        }
    }

    /**
     * Updates the development menu item title based on whether the selected installation is a production installation.
     * For biogas production installations, shows "Biogasproduktion" instead of "Forbrugsudvikling".
     * @param isProductionInstallation - true if the current installation is a biogas production installation
     */
    private updateDevelopmentMenuTitle(isProductionInstallation: boolean) {
        const newTitle = isProductionInstallation ? MENU_TITLE_BIOGAS_PRODUCTION : MENU_TITLE_CONSUMPTION

        this.menu
            .filter((menuItem) => menuItem.id === MenuItemId.consumption)
            .forEach((item) => {
                const developmentSubmenu = item.submenu?.find((i) => i.id === MenuItemId.development)
                if (developmentSubmenu) {
                    developmentSubmenu.title = newTitle
                }
            })

        // Notify subscribers about the menu change
        this.menuItems.next(this.menu)
    }
}
